import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import { FaDownload, FaSpinner } from 'react-icons/fa';
import qrBackground from "../../../../assests/updatedQr.png";
import grouplogo from "../../../../assests/grouplogo1.svg";
import './QRCodeModal.css';

const QRCodeModal = ({ open, onClose, uid }) => {
  const [qrLoaded, setQrLoaded] = useState(false);
  const [qrImageData, setQrImageData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [autoDownloadTriggered, setAutoDownloadTriggered] = useState(false);
  const modalContentRef = useRef(null);

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=10&data=${encodeURIComponent(`https://www.daalchini.co.in/uuid?value=${uid}`)}`;

  // Convert QR to base64
  useEffect(() => {
    if (open) {
      setQrLoaded(false);
      setAutoDownloadTriggered(false);
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        setQrImageData(canvas.toDataURL('image/png'));
        setQrLoaded(true);
      };
      img.onerror = () => {
        setQrLoaded(true);
      };
      img.src = qrCodeUrl;
    }
  }, [open, qrCodeUrl]);

  // Auto Download - When QR loads and modal is open
  useEffect(() => {
    if (open && qrLoaded && !downloading && !autoDownloadTriggered) {
      console.log("✅ Auto download triggered");
      setAutoDownloadTriggered(true);
      setTimeout(() => {
        downloadPDF();
      }, 1500);
    }
  }, [open, qrLoaded]);

  // 🔥 PDF Download - Direct Canvas Approach
  const downloadPDF = async () => {
    if (!modalContentRef.current) {
      setSnackbarMessage('Failed to capture content');
      setSnackbarOpen(true);
      return;
    }

    if (downloading) return;

    setDownloading(true);
    setSnackbarMessage('Generating PDF...');
    setSnackbarOpen(true);

    try {
      const jsPDF = (await import('jspdf')).default;
      
      const element = modalContentRef.current;
      
      // 🔥 Get background image
      const bgImg = new Image();
      bgImg.crossOrigin = "anonymous";
      bgImg.src = qrBackground;
      
      // 🔥 Get QR image
      const qrImg = new Image();
      qrImg.crossOrigin = "anonymous";
      qrImg.src = qrImageData || qrCodeUrl;
      
      // 🔥 Get logo image
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.src = grouplogo;

      // 🔥 Wait for all images to load
      await Promise.all([
        new Promise((resolve) => { bgImg.onload = resolve; bgImg.onerror = resolve; }),
        new Promise((resolve) => { qrImg.onload = resolve; qrImg.onerror = resolve; }),
        new Promise((resolve) => { logoImg.onload = resolve; logoImg.onerror = resolve; })
      ]);

      // 🔥 Create canvas with proper dimensions
      const canvas = document.createElement('canvas');
      const cardWidth = 378;
      const cardHeight = 600;
      const scale = 3;
      canvas.width = cardWidth * scale;
      canvas.height = cardHeight * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);

      // 🔥 Draw Background
      ctx.drawImage(bgImg, 0, 0, cardWidth, cardHeight);

      // 🔥 Draw QR Code
      const qrSize = 255;
      const qrX = (cardWidth - qrSize) / 2;
      const qrY = (cardHeight - qrSize) / 2 - 95;

    
      
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

      // 🔥 Draw Center Logo - NO BACKGROUND (Sirf image)
      const logoSize = 48;
      const logoX = qrX + (qrSize - logoSize) / 2;
      const logoY = qrY + (qrSize - logoSize) / 2;
      
      // 🔥 Sirf logo image draw karo, background nahi
      ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);

      // 🔥 Draw UID Text - LEFT ALIGN
      const uidText = `${uid}`;
      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = '#1e293b';
      ctx.textAlign = 'left';  // 🔥 Left align
      ctx.textBaseline = 'middle';
      const textX = 176;  // 🔥 Left se 40px
      const textY = qrY + qrSize + 19;
      ctx.fillText(uidText, textX, textY);

      // 🔥 Create PDF
      const imgData = canvas.toDataURL('image/png', 0.95);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = 210;
      const pdfHeight = 297;
      
      const aspectRatio = canvas.width / canvas.height;
      let imgWidth = pdfWidth - 18;
      let imgHeight = imgWidth / aspectRatio;
      
      if (imgHeight > pdfHeight - 18) {
        imgHeight = pdfHeight - 18;
        imgWidth = imgHeight * aspectRatio;
      }
      
      const xPosition = (pdfWidth - imgWidth) / 2;
      const yPosition = (pdfHeight - imgHeight) / 2;
      
      pdf.addImage(imgData, 'PNG', xPosition, yPosition, imgWidth, imgHeight);
      pdf.save(`Daalchini_QR_${uid}.pdf`);
      
      setSnackbarMessage('PDF Downloaded Successfully!');
      setSnackbarOpen(true);
      setDownloading(false);
      
    } catch (error) {
      console.error("PDF generation error:", error);
      setSnackbarMessage('Failed to generate PDF. Please try again.');
      setSnackbarOpen(true);
      setDownloading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleManualDownload = () => {
    if (!downloading && qrLoaded) {
      downloadPDF();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '20px',
            overflow: 'hidden',
            maxWidth: '450px',
          },
        }}
      >
        <DialogContent ref={modalContentRef} sx={{ p: 0 }}>
          <Box className="qr-modal-container">
            {/* Background Image */}
            <div className="qr-background-wrapper">
              <img 
                src={qrBackground}
                alt="QR Background"
                className="qr-background-image"
                crossOrigin="anonymous"
              />
              
              {/* QR Code - Overlay on blank space */}
              <div className="qr-code-overlay">
                <div className="qr-code-box">
                  {!qrLoaded && (
                    <div className="qr-loader">
                      <CircularProgress size={35} sx={{ color: '#FF8B36' }} />
                    </div>
                  )}
                  <img
                    src={qrImageData || qrCodeUrl}
                    alt="QR Code"
                    className="qr-code-image"
                    style={{ opacity: qrLoaded ? 1 : 0.5 }}
                    crossOrigin="anonymous"
                  />
                  
                  {/* Center Logo - QR code ke center mein */}
                  <div className="qr-center-logo">
                    <img 
                      src={grouplogo} 
                      alt="Daalchini" 
                      className="qr-center-logo-image"
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>

              {/* UID Text - Below QR */}
              <div className="qr-uid-overlay">
                <span className="qr-uid-text">{uid}</span>
              </div>
            </div>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={downloading ? 'info' : 'success'} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QRCodeModal;